/**
 * Validations tab for a General Process — the SAME structured validation
 * framework as the transaction designer (Built-in / Expression / Business
 * Logic / Call API, severity E/W/P), reusing `ValidationRuleEditor` verbatim.
 * Rules are stored on `process.validations` and run over the result rows on
 * Execute (errors block, warnings → ignore & proceed, prompts inform).
 */

import { useState } from 'react'
import { Pencil, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import type { Validation, ValidationRule } from '@/pages/designers/transaction/types'
import ValidationRuleEditor from '@/pages/designers/transaction/ValidationRuleEditor'
import { SectionHeader, emptyHintStyle, hintStyle } from './ui'

const SEV: Record<string, { label: string; color: string }> = {
  E: { label: 'Error', color: '#ef4444' },
  W: { label: 'Warning', color: '#f5a524' },
  P: { label: 'Prompt', color: '#3b82f6' },
}

interface Props {
  validations: Validation[]
  /** Result columns ({form.X} tokens / pickers). */
  formColumns: string[]
  /** Criteria names ({header.X} tokens). */
  headerColumns?: string[]
  project: string
  onChange: (next: Validation[]) => void
  onOpenCode?: (method: string) => void
}

function asRule(v: Validation): ValidationRule | null {
  return v && typeof v === 'object' ? (v as ValidationRule) : null
}

function summarise(v: Validation): string {
  const r = asRule(v)
  if (!r) return String(v || '')
  if (r.type === 'builtin') return `built-in · ${r.builtin?.kind || '?'}`
  if (r.type === 'expression') return `expression · ${r.expression || '—'}`
  if (r.type === 'business_logic') return `business logic · ${r.business_logic?.method || '—'}`
  if (r.type === 'rest_api') return `call API · ${r.rest_api?.url || '—'}`
  return r.type || '—'
}

export default function ValidationsPanel({
  validations, formColumns, headerColumns = [], project, onChange, onOpenCode,
}: Props) {
  // null = closed; -1 = adding; >=0 = editing that index.
  const [editIdx, setEditIdx] = useState<number | null>(null)

  const list = validations || []
  const move = (i: number, d: number) => {
    const j = i + d
    if (j < 0 || j >= list.length) return
    const next = list.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }
  const remove = (i: number) => {
    if (!confirm(`Delete validation "${(asRule(list[i])?.name) || i + 1}"?`)) return
    onChange(list.filter((_, idx) => idx !== i))
  }
  const onSave = (val: Validation) => {
    if (editIdx == null) return
    const next = list.slice()
    if (editIdx < 0) next.push(val)
    else next[editIdx] = val
    onChange(next)
    setEditIdx(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <SectionHeader>Validations — checked on Execute</SectionHeader>
        <div style={{ flex: 1 }} />
        <button className="btn btn-secondary btn-sm" onClick={() => setEditIdx(-1)}>
          <Plus size={13} /> Add validation
        </button>
      </div>

      {list.length === 0 ? (
        <div style={emptyHintStyle}>
          No validations yet. Add rules (built-in must-exist / unique / range,
          an expression, business logic, or an API call) — they run over every
          result row when you Execute. Errors block; warnings can be ignored;
          prompts inform.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 6 }}>
          {list.map((v, i) => {
            const r = asRule(v)
            const sev = SEV[(r?.severity as string) || 'E'] || SEV.E
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderBottom: i < list.length - 1 ? '1px solid var(--color-border)' : undefined,
                opacity: r?.disabled ? 0.55 : 1,
              }}>
                <span style={{
                  fontSize: 13, fontWeight: 700, padding: '1px 6px', borderRadius: 9,
                  background: sev.color, color: '#fff',
                }} title={sev.label}>{(r?.severity as string) || 'E'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {r?.name || (typeof v === 'string' ? '(legacy)' : '(unnamed)')}
                    {r?.error_code ? <span style={{ ...hintStyle, marginTop: 0, marginLeft: 6 }}>[{r.error_code}]</span> : null}
                  </div>
                  <div style={{ ...hintStyle, marginTop: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {summarise(v)}
                  </div>
                </div>
                <button className="btn btn-ghost btn-sm" style={{ padding: '2px 5px' }} disabled={i === 0} onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                <button className="btn btn-ghost btn-sm" style={{ padding: '2px 5px' }} disabled={i === list.length - 1} onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditIdx(i)}><Pencil size={13} /></button>
                <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444', padding: '2px 5px' }} onClick={() => remove(i)}><Trash2 size={13} /></button>
              </div>
            )
          })}
        </div>
      )}

      {editIdx !== null && (
        <ValidationRuleEditor
          open
          onClose={() => setEditIdx(null)}
          value={editIdx >= 0 ? list[editIdx] : ''}
          onSave={onSave}
          formColumnNames={formColumns}
          headerColumnNames={headerColumns}
          valueColumnNames={formColumns}
          formList={[]}
          project={project}
          onOpenCode={onOpenCode}
        />
      )}
    </div>
  )
}
